import { useState, useEffect } from 'react';
import EverTrue from 'app';
import PropTypes from 'prop-types';
import ReactHtmlParser from 'react-html-parser';
import { getMomentObjectInTimezone } from 'base/new-utils';
import EmailRecipients from '../../components/card/email-recipients';
import { Icon } from '@evertrue/et-components';
import { useScheduledEmails } from '../scheduled-email-context';
import { useGlobalDrawerProvider } from 'components/drawer/context/global-drawer-context';
import { useEmailDrafts } from '../../drafts/email-drafts-context';
import AttachmentIndicator from '../../components/indicators/attachment-indicator';

const RECIPIENTS_PAGE_SIZE = 25;

const ScheduledDetailedView = ({ email = {} }) => {
  const { email_recipients, scheduled_at, body, email_attachments = [] } = email;
  const [recipientPage, setRecipientPage] = useState(0);

  useEffect(() => {
    setRecipientPage(0);
  }, [email?.id]);

  const { cancelScheduled } = useScheduledEmails();
  const { addDrawer } = useGlobalDrawerProvider();
  const { getAllDraftsByUser } = useEmailDrafts();

  const cancelSend = async () => {
    const confirmed = await new Promise((resolve) => {
      EverTrue.Alert.confirm(
        {
          headline: 'Cancel Scheduled Send',
          content: `Are you sure you want to cancel the scheduled send?`,
          buttonLabel: 'Submit',
        },
        (confirmation) => resolve(confirmation)
      );
    });
    if (confirmed) {
      const res = await cancelScheduled(email.id);
      // Refresh draft count since the canceled scheduled email becomes a draft
      await getAllDraftsByUser();
      addDrawer('emailer', {
        drawerTitle: `Edit - Draft`,
        email: res,
      });
      EverTrue.UrlManager.navigate('/outreach/email/drafts', true, false);
    }
  };

  const recipientCount = email_recipients?.length || 0;
  const needsPagination = recipientCount > RECIPIENTS_PAGE_SIZE;
  const totalPages = Math.ceil(recipientCount / RECIPIENTS_PAGE_SIZE);
  const visibleRecipients = needsPagination
    ? email_recipients.slice(recipientPage * RECIPIENTS_PAGE_SIZE, (recipientPage + 1) * RECIPIENTS_PAGE_SIZE)
    : email_recipients;

  return (
    <>
      <div className="scheduled-detail-view--header-container ">
        <span>
          <Icon icon="circle" iconKit="gt2" style={{ color: '#939393', marginRight: '3px', fontSize: '16px' }} />
          Scheduled Send for {getMomentObjectInTimezone(scheduled_at * 1000)?.format('MMM D, YYYY [at] h:mm A z')}
        </span>
        <span onClick={cancelSend} style={{ color: '#007c9e', cursor: 'pointer' }}>
          Cancel Send
        </span>
      </div>
      <div className="scheduled-detail-view--body">
        <EmailRecipients recipients={visibleRecipients} />
        {needsPagination && (
          <div className="scheduled-detail-view--recipient-pagination">
            <span>
              Showing {recipientPage * RECIPIENTS_PAGE_SIZE + 1}-
              {Math.min((recipientPage + 1) * RECIPIENTS_PAGE_SIZE, recipientCount)} of {recipientCount} recipients
            </span>
            <div className="scheduled-detail-view--recipient-pagination-controls">
              <button type="button" disabled={recipientPage === 0} onClick={() => setRecipientPage((p) => p - 1)}>
                Previous
              </button>
              <span>
                Page {recipientPage + 1} of {totalPages}
              </span>
              <button
                type="button"
                disabled={recipientPage >= totalPages - 1}
                onClick={() => setRecipientPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
        <div className="content-preview">{ReactHtmlParser(body)}</div>
        <hr className="email-card-divider" />
        <div className="outreach-card-tab-group">
          {email_attachments.length > 0 &&
            email_attachments.map((attachment, index) => (
              <AttachmentIndicator key={attachment.content_id || index} attachment={attachment} />
            ))}
        </div>
      </div>
    </>
  );
};

ScheduledDetailedView.propTypes = {
  email: PropTypes.object,
};

export default ScheduledDetailedView;
